export interface INoAutofillOptions {
  /**
   * Sensitive fields (passwords, secrets, API keys, OAuth client secrets) use
   * the `new-password` hint, which Chrome honors as "never autofill a stored
   * value here". Plain text credential fields use `off`.
   */
  sensitive?: boolean;
}

export interface INoAutofillProps {
  autoComplete: string;
  'data-lpignore': 'true';
  'data-1p-ignore': 'true';
  'data-form-type': 'other';
  'data-bwignore': 'true';
}

/**
 * Attributes that prevent browsers and password managers from autofilling
 * credential input fields.
 *
 * Modern Chrome ignores `autocomplete="off"` on fields it heuristically
 * classifies (e.g. "client id", "access key"), which lets a value saved for one
 * site leak into an unrelated credential field on another. To resist this we:
 *
 * - use `autocomplete="new-password"` on sensitive fields (Chrome treats it as
 *   "do not autofill a saved value") and `off` on plain text fields, and
 * - opt out of the major password managers via their ignore attributes.
 *
 * Spread the result onto a credential `Input`:
 *
 * ```tsx
 * <Input {...field} {...getNoAutofillProps({ sensitive: true })} />
 * ```
 */
export function getNoAutofillProps(
  options: INoAutofillOptions = {},
): INoAutofillProps {
  return {
    autoComplete: options.sensitive ? 'new-password' : 'off',
    'data-lpignore': 'true', // LastPass
    'data-1p-ignore': 'true', // 1Password
    'data-form-type': 'other', // Dashlane
    'data-bwignore': 'true', // Bitwarden
  };
}
